import { Injectable, inject } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { FloatingPanelDockZone, FloatingPanelRef } from '@shared/floating-panel/floating-panel.types';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdIncarnationCross } from '../basic-properties/hd-chart-properties.types';
import { buildHdCrossKey } from '../basic-properties/hd-incarnation-cross-names';
import {
  HdIncarnationCrossInfoPanelComponent,
  HdIncarnationCrossInfoPanelData,
} from './hd-incarnation-cross-info-panel.component';
import { HdIncarnationCrossGeneralPanelComponent } from './hd-incarnation-cross-general-panel.component';

/** Waar een paneel landt: de dock-zone en of het gedockt of zwevend opent. */
export interface HdIncarnationCrossPanelPlacement {
  readonly dockZone?: FloatingPanelDockZone;
  readonly docked: boolean;
}

export const HD_INCARNATION_CROSS_GENERAL_PANEL_KEY = 'incarnation-cross:general';

export function buildHdIncarnationCrossPanelKey(cross: Pick<HdIncarnationCross, 'gates' | 'angleKey'>): string {
  const [pSun, pEarth, dSun, dEarth] = cross.gates;
  return `incarnation-cross:${cross.angleKey}:${buildHdCrossKey(pSun, pEarth, dSun, dEarth)}`;
}

/** Tab-titel "IX 13/7 | 1/2": kort, en twee open kruisen blijven onderscheidbaar. */
export function buildHdIncarnationCrossTabLabel(cross: Pick<HdIncarnationCross, 'gates'>): string {
  const [pSun, pEarth, dSun, dEarth] = cross.gates;
  return `IX ${pSun}/${pEarth} | ${dSun}/${dEarth}`;
}

/** Plaatsing van een paneel dat vanuit een ander paneel geopend wordt: dezelfde zone en staat. */
export function hdIncarnationCrossPlacementFrom(origin: FloatingPanelRef | null): HdIncarnationCrossPanelPlacement {
  return { dockZone: origin?.dockZone(), docked: origin?.state() === 'docked' };
}

/**
 * Opent de incarnatiekruis-panelen: één paneel per kruis (sleutel per hoek en
 * vier poorten, dus een tweede chart met een ander kruis krijgt een eigen
 * paneel) en één gedeeld paneel met de algemene uitleg en de lijst van alle
 * kruisen. Heropenen van een al open kruis vervangt de data (bv. een ander
 * profiel op hetzelfde kruis).
 */
@Injectable({ providedIn: 'root' })
export class HdIncarnationCrossPanelService {
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  openCross(data: HdIncarnationCrossInfoPanelData, placement: HdIncarnationCrossPanelPlacement): void {
    this.floatingPanels.openOrFocus<HdIncarnationCrossInfoPanelComponent, HdIncarnationCrossInfoPanelData>({
      key: buildHdIncarnationCrossPanelKey(data.cross),
      component: HdIncarnationCrossInfoPanelComponent,
      data,
      dockTab: { label: buildHdIncarnationCrossTabLabel(data.cross) },
      dockZone: placement.dockZone,
      closable: true,
      openInitiallyDocked: placement.docked,
      redockOnReopen: placement.docked,
      // Boektekst-paneel: gecentreerd openen, zoals het profiel-info-paneel.
      startPosition: 'center',
    });
  }

  openGeneral(placement: HdIncarnationCrossPanelPlacement): void {
    this.floatingPanels.openOrFocus<HdIncarnationCrossGeneralPanelComponent, null>({
      key: HD_INCARNATION_CROSS_GENERAL_PANEL_KEY,
      component: HdIncarnationCrossGeneralPanelComponent,
      data: null,
      dockTab: {
        label: this.gateInfo.crossesInfoTextIn('generalTabLabel', this.contentLang.effectiveLang()) || 'IX',
      },
      dockZone: placement.dockZone,
      closable: true,
      openInitiallyDocked: placement.docked,
      redockOnReopen: placement.docked,
      startPosition: 'center',
    });
  }
}
